import { Badge, Button, ButtonGroup, DatePicker, EditNationalId, EditPassport, EditableInput, LabelValue, Toast } from "Components";
import { useEffect, useState } from "react";
import { FaTrashAlt } from "react-icons/fa";
import { FiMail } from "react-icons/fi";
import { get, patch } from "Utils/axios";
import { passengerTypeFromDob, StampToDate, validatePhoneNumber } from "Utils/utils";
import moment from "moment";

export const TravellerCard = ({ mandatoryField, id, isGuest, removeTraveller, airlineCode, updateTraveller, resetErrors }) => {
	const [TravellerDetails, setTravellerDetails] = useState({});

	useEffect(() => {
		fetchDetails();
	}, [id]);

	const fetchDetails = () => {
		if (isGuest) fetchGuest(id);
		else fetchUser(id);
	};

	const fetchUser = (id) => {
		get(`users/${id}`, {}, (r, e) => {
			if (r) {
				setTravellerDetails(r);
				updateTraveller(r);
			}
		});
	};

	const fetchGuest = (id) => {
		get(`guests/${id}`, {}, (r, e) => {
			if (r) {
				setTravellerDetails({ ...r, isGuest: true });
				updateTraveller({ ...r, isGuest: true });
			}
		});
	};

	const onUpdate = async (obj) => {
		if (isGuest) {
			return Toast.error("Guest profile editing is not available here yet");
		}
		const dismiss = Toast.loading("Updating...");
		if (obj.phone && TravellerDetails.phoneCode && !(await validatePhoneNumber(obj.phone, TravellerDetails.phoneCode))) {
			dismiss();
			return Toast.error("Please enter valid phone number");
		}
		patch(`/users/${id}`, obj, (r, e) => {
			dismiss();
			if (r) {
				Toast.success("Traveller updated");
				fetchDetails();
				resetErrors();
			} else if (e) {
				Toast.handleError(e);
			}
		});
	};

	return (
		<div className="my-4 border rounded-md fade-in-up overflow-visible">
			{/* Header: identity + contact + action — wraps on small screens so Remove is never cut off */}
			<div className="p-3 md:p-4 bg-light rounded-t-md flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="min-w-0 flex-1">
					<h5 className="font-semibold text-dark truncate">
						{TravellerDetails.fName} {TravellerDetails.lName} {isGuest ? "(Guest)" : ""}
						{passengerTypeFromDob(TravellerDetails.dob, airlineCode) === "ADULT" && " - Adult"}
						{passengerTypeFromDob(TravellerDetails.dob, airlineCode) === "CHILD" && " - Child"}
						{passengerTypeFromDob(TravellerDetails.dob, airlineCode) === "INFANT" && " - Infant"}
					</h5>
				</div>
				<div className="flex flex-wrap items-center gap-2 min-w-0">
					<span className="flex items-center gap-2 text-label min-w-0 truncate" title={TravellerDetails.email}>
						<FiMail className="shrink-0" aria-hidden />
						<span className="truncate">{TravellerDetails.email}</span>
					</span>
					<Button onClick={() => removeTraveller(id, isGuest)} variant="danger" className="btn-xs shrink-0">
						<FaTrashAlt className="mr-2" aria-hidden />
						Remove
					</Button>
				</div>
			</div>
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-3 md:p-4">
				<LabelValue label="Phone">
					{isGuest ? (
						TravellerDetails.phone || <Badge variant="warning">Missing</Badge>
					) : (
						<>
							<EditableInput key={TravellerDetails.phone} type="number" required onUpdate={(phone) => onUpdate({ phone })} value={TravellerDetails.phone} label="Phone Number" />
							{TravellerDetails.phone || <Badge variant="warning">Missing</Badge>}
						</>
					)}
				</LabelValue>
				<LabelValue label="Gender">
					{isGuest ? (
						TravellerDetails.gender || <Badge variant="warning">Missing</Badge>
					) : (
						<ButtonGroup
							size="xs"
							selected={{
								label: TravellerDetails.gender,
								value: TravellerDetails.gender,
							}}
							onClick={(obj) => onUpdate({ gender: obj.value })}
							options={[
								{ label: "Male", value: "MALE" },
								{ label: "Female", value: "FEMALE" },
							]}
						/>
					)}
				</LabelValue>
				<LabelValue label="Date of Birth">
					{isGuest ? (
						StampToDate(TravellerDetails.dob) || <Badge variant="warning">Missing</Badge>
					) : (
						<DatePicker btnClass="!py-1 !text-xs max-w-min" date={TravellerDetails.dob ? moment(TravellerDetails.dob).valueOf() : undefined} setDate={(dob) => onUpdate({ dob: moment(dob).toDate() })} backDateSelection maxDate={moment().subtract(18, "years").valueOf()} />
					)}
				</LabelValue>
				{mandatoryField.passportValidation && (
					<LabelValue label="Passport (Required)">
						<EditPassport
							reload={() => {
								fetchDetails();
								resetErrors();
							}}
							identification={TravellerDetails.identification}
							userId={isGuest ? undefined : id}
							guestId={isGuest ? id : undefined}
							photo={TravellerDetails.photo}
						/>
					</LabelValue>
				)}
				{mandatoryField.nationalityValidation && (
					<LabelValue label="Government ID (Required)">
						<EditNationalId
							reload={() => {
								fetchDetails();
								resetErrors();
							}}
							identification={TravellerDetails.identification}
							userId={isGuest ? undefined : id}
							guestId={isGuest ? id : undefined}
						/>
					</LabelValue>
				)}
			</div>
		</div>
	);
};
